declare global {
  // eslint-disable-next-line no-var
  var __portalUserMenuSetupDone: boolean | undefined;
}

// -----------------------------
// User Menu (avatar) interactions
// -----------------------------

const SELECTORS = {
  avatar: '[data-id="portal-user-avatar"]',
  // Support both explicit data attribute and class fallback
  dialog: 'dialog[data-dialog-menu], dialog.portal__header-user-menu',
} as const;

type AvatarWithMenu = {
  avatar: HTMLElement;
  dialog: HTMLDialogElement;
};

function getAvatarMenus(): AvatarWithMenu[] {
  const avatars = Array.from(document.querySelectorAll<HTMLElement>(SELECTORS.avatar));
  return avatars.flatMap(avatar => {
    // Find dialog as next sibling
    const dialog = avatar.nextElementSibling?.matches(SELECTORS.dialog)
      ? (avatar.nextElementSibling as HTMLDialogElement)
      : null;
    return dialog ? [{ avatar, dialog } as AvatarWithMenu] : [];
  });
}

function closeAllUserMenus(menus: AvatarWithMenu[], except?: HTMLDialogElement) {
  menus.forEach(({ avatar, dialog }) => {
    if (dialog !== except && dialog.open) {
      dialog.close();
      avatar.setAttribute('aria-expanded', 'false');
      avatar.classList.remove('is-open');
    }
  });
}

function isEventInside(target: EventTarget | null, elements: HTMLElement[]): boolean {
  if (!(target instanceof Node)) return false;
  return elements.some(el => el.contains(target));
}

export function setupUserMenus() {
  // Idempotent guard in case this script executes more than once
  if (globalThis.__portalUserMenuSetupDone) return;
  globalThis.__portalUserMenuSetupDone = true;

  const menus = getAvatarMenus();
  if (menus.length === 0) return;

  menus.forEach(({ avatar, dialog }, index) => {
    // Accessibility hints
    avatar.setAttribute('aria-haspopup', 'menu');
    avatar.setAttribute('aria-expanded', 'false');

    // Ensure the dialog has an id and connect aria-controls
    if (!dialog.id) dialog.id = `portal-user-menu-${index}`;
    avatar.setAttribute('aria-controls', dialog.id);

    // Toggle on avatar click
    const onAvatarClick = (e: Event) => {
      e.preventDefault();
      const willOpen = !dialog.open;
      closeAllUserMenus(menus);
      if (willOpen) {
        // Non-modal to avoid focus trapping inside mobile dialog
        dialog.show();
        avatar.setAttribute('aria-expanded', 'true');
        avatar.classList.add('is-open');
      } else {
        dialog.close();
        avatar.setAttribute('aria-expanded', 'false');
        avatar.classList.remove('is-open');
      }
    };

    avatar.addEventListener('click', onAvatarClick);

    // Toggle with Enter/Space for accessibility
    const onAvatarKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onAvatarClick(e);
      }
    };
    avatar.addEventListener('keydown', onAvatarKeyDown);

    // Close when clicking a menu item
    dialog.addEventListener('click', evt => {
      const target = evt.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('a, button, [type="submit"], form')) {
        // Let navigation/submission happen, just close visually
        dialog.close();
        avatar.setAttribute('aria-expanded', 'false');
        avatar.classList.remove('is-open');
      }
    });
  });

  // Close on outside click
  const onDocPointer = (evt: Event) => {
    const targets: HTMLElement[] = menus.flatMap(({ avatar, dialog }) => [avatar, dialog]);
    if (!isEventInside(evt.target, targets)) {
      closeAllUserMenus(menus);
    }
  };
  document.addEventListener('pointerdown', onDocPointer);

  // Close on Escape
  const onKey = (evt: KeyboardEvent) => {
    if (evt.key === 'Escape') {
      closeAllUserMenus(menus);
    }
  };
  document.addEventListener('keydown', onKey);
}

// Initialize user menus as soon as DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupUserMenus);
} else {
  setupUserMenus();
}
